// Cajas / terminales del POS. POS → Configuración → Cajas.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Monitor, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Caja, Campo, CentroContableOpt, LocationOpt, Modal, PartnerOpt, Switch, centrosCostoList, centrosContablesList, fieldCls, locationsList, pos, posRpc } from './shared';
import { ptoListar, type Punto } from '../financiero/EstablecimientosPuntos';

const vacio = {
  codigo: '', nombre: '', location_id: '', centro_costo_id: '', cc_contable_id: '', establecimiento: '', establecimiento_id: '', punto_emision: '', punto_emision_id: '', ambiente: '', es_activo: true,
  modo_impresion: 'terminal' as 'terminal' | 'red', impresora_host: '', impresora_puerto: '9100',
  permite_nota_venta: true,
};

export function Cajas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Caja[]>({ queryKey: ['pos-cajas'], queryFn: () => pos.list<Caja>('cajas', '?select=*&order=nombre') });
  const { data: locations = [] } = useQuery<LocationOpt[]>({ queryKey: ['pos-locations'], queryFn: locationsList });
  const { data: centros = [] } = useQuery<PartnerOpt[]>({ queryKey: ['pos-centros'], queryFn: centrosCostoList });
  const { data: centrosContables = [] } = useQuery<CentroContableOpt[]>({ queryKey: ['pos-centros-contables'], queryFn: centrosContablesList });
  const { data: puntos = [] } = useQuery<Punto[]>({ queryKey: ['puntos-emision'], queryFn: ptoListar });
  const locName = (id: string | null) => locations.find((l) => l.id === id)?.nombre ?? '—';

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [secuencial, setSecuencial] = useState(''); // último secuencial emitido de la serie de la caja
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = async (c?: Caja) => {
    if (c) {
      setEditId(c.id); setForm({ codigo: c.codigo ?? '', nombre: c.nombre, location_id: c.location_id ?? '', centro_costo_id: c.centro_costo_id ?? '', cc_contable_id: (c as any).cc_contable_id ?? '', establecimiento: c.establecimiento ?? '', establecimiento_id: (c as any).establecimiento_id ?? '', punto_emision: c.punto_emision ?? '', punto_emision_id: (c as any).punto_emision_id ?? '', ambiente: c.ambiente ?? '', es_activo: c.es_activo, modo_impresion: c.modo_impresion ?? 'terminal', impresora_host: c.impresora_host ?? '', impresora_puerto: c.impresora_puerto != null ? String(c.impresora_puerto) : '9100', permite_nota_venta: c.permite_nota_venta ?? true });
      setSecuencial('');
      try { setSecuencial(String(await posRpc<number>('pos_caja_secuencial_get', { p_caja: c.id }))); } catch { /* sin serie aún */ }
    } else { setEditId(null); setForm(vacio); setSecuencial(''); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      // La serie (estab+punto) de cada caja debe ser única: dos cajas con la misma
      // serie compartirían el contador de secuencial y emitirían números repetidos.
      if (form.establecimiento && form.punto_emision) {
        const choca = rows.some((c) => c.id !== editId && c.establecimiento === form.establecimiento && c.punto_emision === form.punto_emision);
        if (choca) throw new Error(`La serie ${form.establecimiento}-${form.punto_emision} ya está asignada a otra caja`);
      }
      const body = {
        ...form, codigo: form.codigo || null, location_id: form.location_id || null, centro_costo_id: form.centro_costo_id || null, cc_contable_id: form.cc_contable_id || null, establecimiento: form.establecimiento || null, establecimiento_id: form.establecimiento_id || null, punto_emision: form.punto_emision || null, punto_emision_id: form.punto_emision_id || null, ambiente: form.ambiente || null,
        impresora_host: form.modo_impresion === 'red' ? (form.impresora_host.trim() || null) : null,
        impresora_puerto: Number(form.impresora_puerto) || 9100,
      };
      const res = editId ? await pos.update('cajas', editId, body) : await pos.create('cajas', body);
      const id = editId ?? (Array.isArray(res) ? (res[0] as Caja)?.id : (res as Caja)?.id);
      // Fija el secuencial inicial de la serie de la caja (si tiene estab+punto).
      if (id && form.establecimiento && form.punto_emision && secuencial !== '') {
        await posRpc('pos_caja_secuencial_set', { p_caja: id, p_secuencial: Number(secuencial) || 0 });
      }
      return res;
    },
    onSuccess: () => { toast.success('Caja guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-cajas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => pos.remove('cajas', id),
    onSuccess: () => { toast.success('Caja eliminada'); void qc.invalidateQueries({ queryKey: ['pos-cajas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Monitor className="w-5 h-5" /> Cajas / Terminales</h1>
        <Button onClick={() => { void abrir(); }}><Plus className="w-4 h-4 mr-1" /> Nueva caja</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cajas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Bodega</th><th className="py-2 pr-3">Estab-PtoEmi</th><th className="py-2 pr-3">Ambiente</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cajas</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono text-xs">{c.codigo ?? '—'}</td>
                  <td className="py-2 pr-3">{c.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{locName(c.location_id)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.establecimiento || '—'}-{c.punto_emision || '—'}</td>
                  <td className="py-2 pr-3">
                    {c.ambiente === '2'
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Producción</span>
                      : c.ambiente === '1'
                        ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Pruebas</span>
                        : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => { void abrir(c); }}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${c.nombre}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar caja' : 'Nueva caja'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            </div>
            <Campo label="Bodega">
              <select className={fieldCls} value={form.location_id} onChange={(e) => set({ location_id: e.target.value })}>
                <option value="">— Sin bodega —</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Centro de costo (para la factura)">
              <select className={fieldCls} value={form.centro_costo_id} onChange={(e) => set({ centro_costo_id: e.target.value })}>
                <option value="">— Sin centro de costo —</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.razon_social}</option>)}
              </select>
            </Campo>
            <Campo label="Centro de costo contable">
              <select className={fieldCls} value={form.cc_contable_id} onChange={(e) => set({ cc_contable_id: e.target.value })}>
                <option value="">— Sin centro de costo contable —</option>
                {centrosContables.map((c) => <option key={c.id} value={c.id}>{c.codigo ? `${c.codigo} · ` : ''}{c.nombre}</option>)}
              </select>
            </Campo>
            <p className="text-xs text-muted-foreground -mt-1">El centro de costo contable etiqueta el asiento del <strong>cierre de caja</strong> (Contabilidad → Centros de costo). El de la factura va en el comprobante.</p>
            <Campo label="Punto de emisión (serie estab-punto)">
              <select className={fieldCls} value={form.punto_emision_id}
                onChange={(e) => { const p = puntos.find((x) => x.id === e.target.value); set({ punto_emision_id: e.target.value, punto_emision: p?.codigo ?? '', establecimiento: p?.est_codigo ?? '', establecimiento_id: p?.establecimiento_id ?? '', ambiente: p?.ambiente ?? form.ambiente }); }}>
                <option value="">— Selecciona —</option>
                {form.punto_emision_id === '' && form.punto_emision && <option value="" disabled>actual: {form.establecimiento}-{form.punto_emision} (sin catálogo)</option>}
                {puntos.map((p) => <option key={p.id} value={p.id}>{p.est_codigo}-{p.codigo}{p.nombre ? ` · ${p.nombre}` : ''} · {p.ambiente === '2' ? 'Producción' : 'Pruebas'}{!p.activo ? ' (inact.)' : ''}</option>)}
              </select>
            </Campo>
            <p className="text-xs text-muted-foreground -mt-1">Los puntos (serie estab-punto) y su ambiente se administran en <strong>Financiero → Configuración de Emisión</strong>. La dirección del establecimiento va en el comprobante.</p>
            <Campo label="Secuencial actual (último emitido en esta serie)">
              <input className={fieldCls} type="number" min={0} placeholder="0" value={secuencial} onChange={(e) => setSecuencial(e.target.value)} disabled={!form.establecimiento || !form.punto_emision} />
            </Campo>
            <p className="text-xs text-muted-foreground -mt-1">Cada caja numera sus facturas de forma independiente bajo su propia serie (estab-punto), sin chocar con la facturación de oficina. La próxima factura usará este número + 1.</p>
            <Campo label="Ambiente SRI">
              <select className={fieldCls} value={form.ambiente} onChange={(e) => set({ ambiente: e.target.value })}>
                <option value="">Heredar de oficina (Configuración de Emisión)</option>
                <option value="1">Pruebas</option>
                <option value="2">Producción</option>
              </select>
            </Campo>
            <p className="text-xs text-muted-foreground -mt-1">Permite emitir esta caja en <strong>Pruebas</strong> mientras la oficina factura en <strong>Producción</strong> (o viceversa). Al pasar de Pruebas a Producción, reinicia el secuencial de arriba para que la numeración de producción arranque limpia.</p>
            <div className="border-t pt-3 space-y-3">
              <h3 className="text-sm font-medium">Impresión</h3>
              <Campo label="Modo de impresión">
                <select className={fieldCls} value={form.modo_impresion} onChange={(e) => set({ modo_impresion: e.target.value as 'terminal' | 'red' })}>
                  <option value="terminal">Terminal (navegador)</option>
                  <option value="red">Red (ESC/POS)</option>
                </select>
              </Campo>
              {form.modo_impresion === 'red' && (
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="IP / host de la impresora">
                    <input className={fieldCls} placeholder="IP de la impresora, ej. 192.168.1.50" value={form.impresora_host} onChange={(e) => set({ impresora_host: e.target.value })} />
                  </Campo>
                  <Campo label="Puerto">
                    <input className={fieldCls} type="number" min={1} placeholder="9100" value={form.impresora_puerto} onChange={(e) => set({ impresora_puerto: e.target.value })} />
                  </Campo>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                En modo Red, el ticket se imprime directo por ESC/POS (sin diálogo del navegador) vía el print-agent.
              </p>
            </div>
            <div className="border-t pt-3 space-y-1">
              <Switch checked={form.permite_nota_venta} onChange={(v) => set({ permite_nota_venta: v })} label="Permitir Notas de venta" />
              <p className="text-xs text-muted-foreground">Si está apagado, esta caja solo emite Facturas (oculta el botón/atajo de Nota de venta en el punto de venta).</p>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activa" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
